import React, { useState, useMemo, useEffect, useCallback, ChangeEvent } from 'react';
import { Button, TextInput, Select, Card, Alert, Text, Loader, Modal, Title, Group, Stack, NumberInput } from '@mantine/core';
import { Bluetooth, Send, Code, ChevronDown, ChevronRight, Wifi, Activity, Usb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { bluetoothService } from '../services/bluetoothService';
import { usbSerialService } from '../services/usbSerialService';
import { Command, SnackbarState, DeviceConfig, DeviceConfigs } from '../types';
import deviceConfigsData from '../config/deviceConfigs.json';

interface DevicePageProps {
  deviceType: 'gateway' | 'sensor';
}

interface CommandHistoryItem {
  command: string;
  timestamp: Date;
  response?: string;
  success: boolean;
}

// Type assertion with validation
const deviceConfigs = deviceConfigsData as DeviceConfigs;

// Validation function for device config
const validateDeviceConfig = (config: unknown): config is DeviceConfig => {
  if (!config || typeof config !== 'object') return false;
  
  const c = config as Record<string, unknown>;
  
  return (
    typeof c.title === 'string' &&
    typeof c.description === 'string' &&
    typeof c.icon === 'string' &&
    typeof c.connectionType === 'string' &&
    typeof c.showDeviceStatus === 'boolean' &&
    typeof c.showCommandHistory === 'boolean' &&
    typeof c.showCommandReference === 'boolean' &&
    (c.layout === 'split' || c.layout === 'stacked') &&
    Array.isArray(c.commands) &&
    typeof c.categories === 'object' &&
    c.categories !== null
  );
};

const DevicePage: React.FC<DevicePageProps> = ({ deviceType }) => {
  const { t } = useTranslation();
  
  // Safe config access with validation
  const rawConfig = deviceConfigs[deviceType];
  
  if (!validateDeviceConfig(rawConfig)) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert color="red" title={t('ui.configError')}>
          {t('ui.invalidConfig', { deviceType })}
        </Alert>
      </div>
    );
  }
  
  const config: DeviceConfig = rawConfig;
  
  // Select appropriate service based on connection type
  const deviceService = config.connectionType === 'usb' ? usbSerialService : bluetoothService;
  
  // State for device connection
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [deviceName, setDeviceName] = useState<string>('');
  
  // State for command input
  const [selectedCommand, setSelectedCommand] = useState<string>('');
  const [commandParam, setCommandParam] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  
  // State for command history and responses
  // const [commandHistory, setCommandHistory] = useState<CommandHistoryItem[]>([]);
  const [currentResponse, setCurrentResponse] = useState<string>('');
  
  // State for UI
  const [showDisconnectDialog, setShowDisconnectDialog] = useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(
    Object.fromEntries(
      Object.entries(config.categories).map(([key, value]) => [key, value.expanded])
    )
  );
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Responsive design
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Command options for select
  const commandOptions = useMemo(() => 
    config.commands.map(cmd => ({
      value: cmd.id,
      label: cmd.label
    }))
  , [config.commands]);

  // Group commands by category
  const commandsByCategory = useMemo(() => {
    const grouped = config.commands.reduce((acc, cmd) => {
      if (!acc[cmd.category]) {
        acc[cmd.category] = [];
      }
      acc[cmd.category].push(cmd);
      return acc;
    }, {} as Record<string, Command[]>);

    // Sort categories by order
    const sortedCategories = Object.entries(grouped).sort(([a], [b]) => {
      const orderA = config.categories[a]?.order || 999;
      const orderB = config.categories[b]?.order || 999;
      return orderA - orderB;
    });

    return Object.fromEntries(sortedCategories);
  }, [config.commands, config.categories]);

  // Get selected command details
  const selectedCommandDetails = useMemo(() => 
    config.commands.find(cmd => cmd.id === selectedCommand)
  , [selectedCommand, config.commands]);

  // Validate parameter
  const validateParam = useCallback((command: Command, value: string): { valid: boolean; error?: string } => {
    if (!command.hasParam) return { valid: true };
    if (!command.validation) return { valid: true };

    const validation = command.validation;

    if (validation.required && !value) {
      return { valid: false, error: validation.errorMessage || t('ui.fieldRequired') };
    }


    if (command.paramType === 'number') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { valid: false, error: t('ui.validNumber') };
      }
      if (validation.min !== undefined && numValue < validation.min) {
        return { valid: false, error: t('ui.valueAtLeast', { min: validation.min }) };
      }
      if (validation.max !== undefined && numValue > validation.max) {
        return { valid: false, error: t('ui.valueBetween', { min: validation.min, max: validation.max }) };
      }
    }

    if (validation.minLength && value.length < validation.minLength) {
      return { valid: false, error: t('ui.minLength', { length: validation.minLength }) };
    }

    return { valid: true };
  }, []);

  // Handle command selection change
  const handleCommandChange = useCallback((value: string | null) => {
    setSelectedCommand(value || '');
    setCommandParam('');
  }, []);

  // Handle parameter change
  const handleParamChange = useCallback((value: string | null) => {
    setCommandParam(value || '');
  }, []);

  // Handle text input parameter change
  const handleTextParamChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCommandParam(e.target.value);
  }, []);

  // Handle number input parameter change
  const handleNumberParamChange = useCallback((value: string | number) => {
    setCommandParam(String(value));
  }, []);

  // Handle connection
  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      // Set up data received callback
      deviceService.onDataReceived = (data: string) => {
        setCurrentResponse(prev => prev ? `${prev}\n${data}` : data);
      };
      
      const success = await deviceService.connect();
      if (success) {
        setIsConnected(true);
        setDeviceName(`${config.title}`);
      }
    } catch (error) {
      // Ignore connection errors
    } finally {
      setIsConnecting(false);
    }
  }, [config.title, deviceService]);

  // Handle disconnect
  const handleDisconnect = useCallback(async () => {
    try {
      deviceService.disconnect();
      setIsConnected(false);
      setDeviceName('');
      setShowDisconnectDialog(false);
    } catch (error) {
      // Ignore disconnect errors
    }
  }, [deviceService]);

  // Handle sending command
  const handleSendCommand = useCallback(async () => {
    if (!selectedCommandDetails) return;
    
    // Validate parameter if needed
    if (selectedCommandDetails.hasParam) {
      const validation = validateParam(selectedCommandDetails, commandParam);
      if (!validation.valid) {
        setSnackbar({
          open: true,
          message: validation.error || 'Invalid parameter',
          severity: 'error'
        });
        return;
      }
    }
    
    setIsSending(true);
    try {
      let commandString = selectedCommandDetails.id;
      if (selectedCommandDetails.hasParam && commandParam) {
        commandString += ` ${commandParam}`;
      }
      
      const success = await deviceService.sendCommand(commandString);
      // Don't set response here - it will come via onDataReceived callback
      
      if (config.showCommandHistory) {
        const _newHistoryItem: CommandHistoryItem = {
          command: commandString,
          timestamp: new Date(),
          success,
          response: '' // Response will be populated by callback
        };
        // setCommandHistory(prev => [newHistoryItem, ...prev]);
      }
    } catch (error) {
      setCurrentResponse(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
    }
  }, [selectedCommandDetails, commandParam, validateParam, config.showCommandHistory, deviceService]);

  // Handle clearing response
  const handleClearResponse = useCallback(() => {
    setCurrentResponse('');
  }, []);

  // Get icon component
  const getIcon = (iconName: string, size = 20, color?: string) => {
    switch (iconName) {
      case 'wifi': return <Wifi size={size} color={color} />;
      case 'activity': return <Activity size={size} color={color} />;
      case 'bluetooth': return <Bluetooth size={size} color={color} />;
      case 'usb': return <Usb size={size} color={color} />;
      default: return <Activity size={size} color={color} />;
    }
  };

  // Render parameter input based on type
  const renderParameterInput = () => {
    if (!selectedCommandDetails?.hasParam) return null;

    const command = selectedCommandDetails;
    const validation = validateParam(command, commandParam);

    switch (command.paramType) {
      case 'select':
        return (
          <Select
            label={t('common.parameter')}
            data={command.options || []}
            value={commandParam}
            onChange={handleParamChange}
            disabled={!isConnected || isSending}
            placeholder={t('common.selectValue')}
            error={!validation.valid ? validation.error : undefined}
          />
        );
      
      case 'number':
        return (
          <NumberInput
            label={t('common.parameter')}
            value={commandParam}
            onChange={handleNumberParamChange}
            disabled={!isConnected || isSending}
            placeholder={t('common.enterValue')}
            min={command.validation?.min}
            max={command.validation?.max}
            error={!validation.valid ? validation.error : undefined}
          />
        );
      
      case 'datetime-local':
        return (
          <TextInput
            type="datetime-local"
            label={t('common.parameter')}
            value={commandParam}
            onChange={handleTextParamChange}
            disabled={!isConnected || isSending}
            error={!validation.valid ? validation.error : undefined}
          />
        );
      
      default:
        return (
          <TextInput
            label={t('common.parameter')}
            value={commandParam}
            onChange={handleTextParamChange}
            disabled={!isConnected || isSending}
            placeholder={t('common.enterValue')}
            error={!validation.valid ? validation.error : undefined}
          />
        );
    }
  };

  const isCommandValid = useMemo(() => {
    if (!selectedCommandDetails) return false;
    if (!selectedCommandDetails.hasParam) return true;
    return validateParam(selectedCommandDetails, commandParam).valid;
  }, [selectedCommandDetails, commandParam, validateParam]);

  return (
    <div className="fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Page Header */}
      <Group justify="space-between" mb="xl">
        <div>
          <Group gap="md" mb="xs" style={{ color: 'var(--tt-dark)' }}>
            {getIcon(config.icon || 'activity', 24)}
            <Title order={2} style={{ fontWeight: 300 }}>
              {config.title}
            </Title>
          </Group>
          <Text c="dimmed">{config.description}</Text>
        </div>

        {/* Connection Button */}
        {!isConnected ? (
          <Button
            variant="filled"
            onClick={handleConnect}
            leftSection={isConnecting ? <Loader size="sm" /> : (config.connectionType === 'usb' ? <Usb /> : <Bluetooth />)}
            disabled={isConnecting}
          >
            {isConnecting ? t('common.connecting') : t('common.connect')}
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowDisconnectDialog(true)}
          >
            {t('common.disconnect')}
          </Button>
        )}
      </Group>

      {/* Main Content */}
      <Stack gap="lg">
        {/* Device Status */}
        {config.showDeviceStatus && isConnected && (
          <Card padding="lg">
            <Title order={3} mb="md">{t('ui.deviceStatus')}</Title>
            {deviceName && (
              <Text size="sm" c="dimmed">
                {t('common.connected', { deviceName })}
              </Text>
            )}
            <Text size="sm" c="green">
              {t('common.status')}: {t('common.online')}
            </Text>
          </Card>
        )}

        {/* Command Interface */}
        <div style={{ 
          display: config.layout === 'split' ? 'flex' : 'block',
          gap: config.layout === 'split' ? '24px' : '0',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Command Input */}
          <Card padding="lg" style={{ flex: config.layout === 'split' ? 1 : undefined, marginBottom: config.layout === 'stacked' ? '24px' : '0' }}>
            <Title order={3} mb="md">{t('ui.sendCommand')}</Title>
            <Text size="sm" c="dimmed" mb="md">
              {t('ui.selectCommandDescription')}
            </Text>
            
            <Stack gap="md">
              <Select
                label={t('common.command')}
                data={commandOptions}
                value={selectedCommand}
                onChange={handleCommandChange}
                disabled={!isConnected}
                placeholder={t('common.selectCommand')}
              />

              {renderParameterInput()}

              <Button
                variant="filled"
                onClick={handleSendCommand}
                disabled={!isConnected || !selectedCommand || isSending || !isCommandValid}
                leftSection={isSending ? <Loader size="sm" /> : <Send />}
                fullWidth={isMobile}
              >
                {isSending ? t('common.sending') : t('common.send')}
              </Button>
            </Stack>
          </Card>

          {/* Response Display */}
          <Card padding="lg" style={{ flex: config.layout === 'split' ? 1 : undefined }}>
            <Group justify="space-between" mb="md">
              <Title order={3}>{t('common.response')}</Title>
              {currentResponse && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearResponse}
                  leftSection={<Code />}
                  disabled={isSending}
                >
                  {t('common.clear')}
                </Button>
              )}
            </Group>
            
            <Card
              padding="md"
              style={{
                backgroundColor: 'var(--timbertrack-background)',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                minHeight: '200px',
                border: '1px solid var(--timbertrack-border)'
              }}
            >
              {currentResponse || t('common.noResponse')}
            </Card>
          </Card>
        </div>

        {/* Command Reference */}
        {config.showCommandReference && (
          <Card padding="lg">
            <Title order={3} mb="md">{t('ui.commandReference')}</Title>
            <div>
              {Object.entries(commandsByCategory).map(([category, cmds]) => (
                <div key={category} style={{ marginBottom: '16px' }}>
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: expandedCategories[category] ? 'var(--timbertrack-background)' : 'transparent'
                    }}
                    onClick={() => setExpandedCategories(prev => ({
                      ...prev,
                      [category]: !prev[category]
                    }))}
                  >
                    {expandedCategories[category] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Text fw={600}>{category}</Text>
                  </div>
                  
                  {expandedCategories[category] && (
                    <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                      {cmds.map(cmd => (
                        <div key={cmd.id} style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'var(--timbertrack-surface)', borderRadius: '8px', border: '1px solid var(--timbertrack-border)' }}>
                          <Text fw={500} mb="xs">{cmd.label}</Text>
                          <Text size="sm" c="dimmed" mb="xs">{cmd.description}</Text>
                          {cmd.hasParam && (
                            <Text size="xs" c="var(--timbertrack-text-secondary)">
                              Parameter: {cmd.paramType}
                              {cmd.options && (
                                <span style={{ marginLeft: '8px' }}>
                                  ({cmd.options.map(opt => opt.label).join(', ')})
                                </span>
                              )}
                            </Text>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </Stack>

      {/* Disconnect Confirmation Modal */}
      <Modal
        opened={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        title={t('common.disconnect')}
      >
        <Text mb="md">
          {t('ui.disconnectConfirm')}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDisconnect}>
            {t('common.disconnect')}
          </Button>
        </Group>
      </Modal>

      {/* Snackbar for validation errors */}
      {snackbar.open && (
        <Alert 
          color={snackbar.severity === 'error' ? 'red' : 'blue'}
          style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}
    </div>
  );
};

export default DevicePage;
